import { productsBySubcategory } from '~/entities/catalog/api';
import { type ListedProduct, toListedProduct } from '~/entities/catalog/listed-product';
import type { Catalog, SubcategoryPage } from '~/entities/catalog/types';
import { getColorFamily } from '~/entities/catalog/variants';
import { buildSidebarCategories, type SidebarCategory } from '~/widgets/catalog-sidebar';

/**
 * What the subcategory page renders, as its route loader returns it: the page's own entry, its category's slug
 * and title, the products it lists and the sidebar, never the whole catalog, so a server render can serialise it
 * with the page. It depends only on the page, not on the address's query: `?sub=`, `?q=` and the filters narrow
 * the list in the component.
 */
export type SubcategoryPageData = {
  page: SubcategoryPage;
  /** The category's URL slug, for the breadcrumb and the sub-filter tile links. */
  categorySlug: string;
  /** The category page's title (a term), for the breadcrumb; missing when the category has no page. */
  categoryTitle?: string;
  /**
   * Every product of the subcategory, plus the colour variants outside it that their cards show as dots, in
   * catalog order (the order of the dots). The page lists only those in the subcategory.
   */
  products: ListedProduct[];
  /** The catalog sidebar with this subcategory's sub-filters expanded. */
  sidebarCategories: SidebarCategory[];
};

export function subcategoryPageData(catalog: Catalog, page: SubcategoryPage): SubcategoryPageData {
  const listed = productsBySubcategory(catalog.products, page.id);
  const shown = new Set(listed.flatMap((product) => getColorFamily(product, catalog.products)));
  for (const product of listed) {
    shown.add(product);
  }

  return {
    page,
    categorySlug: catalog.categories.find((category) => category.id === page.categoryId)?.slug ?? page.categoryId,
    categoryTitle: catalog.categoryPages.find((categoryPage) => categoryPage.id === page.categoryId)?.title,
    products: catalog.products.filter((product) => shown.has(product)).map(toListedProduct),
    sidebarCategories: buildSidebarCategories(catalog, { activeSubcategoryId: page.id }),
  };
}
